/**
 * AdminAgenticConsole.jsx — Autonomous-runtime cockpit.
 *
 * Tabs:
 *   1. Inbox            — founder inbox with status + severity filters
 *   2. Orchestrator     — loop runs history + manual trigger
 *   3. Subscribers      — message-bus topic map
 *   4. Tools            — callable tool registry with agent permissions
 *   5. Self-Improvement — pending weekly policy proposals
 *
 * Polls every 30s on Inbox & Loop tabs.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import {
  Inbox, RefreshCw, Play, Check, X, Radio,
  Zap, Activity, Wrench, Sparkles, Send,
  Bell, Clock, AlertTriangle, ChevronRight,
  CheckCircle2, XCircle,
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminAgenticConsole.css';

// ── constants ───────────────────────────────────────────────────────────────
const STATUS_FILTERS = [
  { k: 'all',          label: 'Tutti' },
  { k: 'live',         label: 'Live' },
  { k: 'pending',      label: 'Pending' },
  { k: 'digested',     label: 'Digest' },
  { k: 'acknowledged', label: 'Ack' },
  { k: 'dismissed',    label: 'Scartati' },
];

const SEVERITY_FILTERS = [
  { k: 'all',      label: 'Tutte' },
  { k: 'critical', label: '🔴 Critical' },
  { k: 'high',     label: '🟠 High' },
  { k: 'medium',   label: '🟡 Medium' },
  { k: 'low',      label: '⚪ Low' },
];

const fmtDate = (d) => d
  ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

const errMsg = (err) => err?.response?.data?.message || err?.message || 'Errore sconosciuto';

// ── component ────────────────────────────────────────────────────────────────
export default function AdminAgenticConsole() {
  const toast = useToast();
  const [tab, setTab]           = useState('inbox');
  const [loading, setLoading]   = useState(false);

  const [inbox, setInbox]           = useState([]);
  const [statusFilter, setStatusFilter]     = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  const [loopLast, setLoopLast]     = useState(null);
  const [loopRecent, setLoopRecent] = useState([]);
  const [subs, setSubs]             = useState([]);
  const [tools, setTools]           = useState([]);
  const [perms, setPerms]           = useState({});

  const timerRef = useRef(null);

  // ── loaders ────────────────────────────────────────────────────────────────
  const loadInbox = useCallback(async () => {
    const p = new URLSearchParams({ limit: '120' });
    if (statusFilter !== 'all') p.set('status', statusFilter);
    const r = await api.get(`/agents/inbox?${p}`);
    setInbox(r.data?.data?.items || []);
  }, [statusFilter]);

  const loadLoop = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get('/agents/loop/last'),
      api.get('/agents/loop/recent?limit=10'),
    ]);
    setLoopLast(a.data?.data   || null);
    setLoopRecent(b.data?.data || []);
  }, []);

  const loadSubs  = useCallback(async () => {
    const r = await api.get('/agents/bus/subscribers');
    setSubs(r.data?.data || []);
  }, []);

  const loadTools = useCallback(async () => {
    const r = await api.get('/agents/runtime/tools');
    setTools(r.data?.data || []);
    setPerms(r.data?.permissions || {});
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if      (tab === 'inbox')   await loadInbox();
      else if (tab === 'loop')    await loadLoop();
      else if (tab === 'subs')    await loadSubs();
      else if (tab === 'tools')   await loadTools();
      else if (tab === 'improve') await loadInbox();
    } catch (err) {
      toast.error(`Errore: ${errMsg(err)}`);
    } finally {
      setLoading(false);
    }
  }, [tab, loadInbox, loadLoop, loadSubs, loadTools, toast]);

  useEffect(() => { refresh(); }, [refresh]);

  // auto-refresh inbox & loop every 30 s
  useEffect(() => {
    clearInterval(timerRef.current);
    if (tab === 'inbox' || tab === 'loop' || tab === 'improve') {
      timerRef.current = setInterval(refresh, 30_000);
    }
    return () => clearInterval(timerRef.current);
  }, [tab, refresh]);

  // ── derived ────────────────────────────────────────────────────────────────
  const filteredInbox = useMemo(() =>
    inbox.filter(i => severityFilter === 'all' || i.severity === severityFilter),
  [inbox, severityFilter]);

  const improvementItems = useMemo(() =>
    inbox.filter(i => i.category === 'self-improvement' && ['pending', 'live'].includes(i.status)),
  [inbox]);

  const stats = useMemo(() => ({
    live:     inbox.filter(i => i.status === 'live').length,
    pending:  inbox.filter(i => i.status === 'pending').length,
    critical: inbox.filter(i => i.severity === 'critical' && ['live', 'pending'].includes(i.status)).length,
    open:     inbox.filter(i => ['live', 'pending'].includes(i.status)).length,
  }), [inbox]);

  // ── actions ────────────────────────────────────────────────────────────────
  const ackItem = async (id) => {
    try { await api.post(`/agents/inbox/${id}/ack`); toast.success('Acknowledged ✅'); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const dismissItem = async (id) => {
    try { await api.post(`/agents/inbox/${id}/dismiss`); toast.info('Scartato'); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const runLoopNow = async () => {
    setLoading(true);
    try {
      const r = await api.post('/agents/loop/run');
      if (r.data?.data?.ok === false) toast.error(`Loop fallito: ${r.data.data.error}`);
      else toast.success('Orchestrator loop completato ✓');
      await loadLoop();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  const fireDigest = async () => {
    try { await api.post('/agents/inbox/digest'); toast.success('Digest inviato 📨'); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">

        {/* Header */}
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Radio size={26} color="#06b6d4" /> Agentic Console
            </h1>
            <p>Cockpit della runtime autonoma — inbox, loop, message bus, tools.</p>
          </div>
          <div className="admin-header-right">
            <button onClick={fireDigest} className="admin-btn admin-btn-secondary">
              <Send size={14} /> Digest
            </button>
            <button onClick={refresh} disabled={loading} className="admin-btn admin-btn-secondary">
              <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="ac-stats-bar">
          <StatTile icon={<Bell size={18} color="#ef4444" />}      bg="rgba(239,68,68,0.12)"
            label="Live"           value={stats.live}     sub="urgenti notificati" />
          <StatTile icon={<Clock size={18} color="#f59e0b" />}     bg="rgba(245,158,11,0.12)"
            label="Pending"        value={stats.pending}  sub="attendono digest" />
          <StatTile icon={<AlertTriangle size={18} color="#ef4444" />} bg="rgba(239,68,68,0.10)"
            label="Critical aperti" value={stats.critical} sub="azione richiesta" />
          <StatTile
            icon={<Zap size={18} color={loopLast?.status === 'completed' ? '#10b981' : '#52525b'} />}
            bg="rgba(16,185,129,0.1)"
            label="Ultimo loop"
            value={loopLast ? loopLast.status : '—'}
            sub={loopLast ? fmtDate(loopLast.createdAt) : 'mai eseguito'} />
          <StatTile icon={<Activity size={18} color="#06b6d4" />}  bg="rgba(6,182,212,0.12)"
            label="Bus topics"     value={subs.length  || '—'} sub="sottoscrizioni attive" />
          <StatTile icon={<Wrench size={18} color="#a855f7" />}    bg="rgba(168,85,247,0.12)"
            label="Tools"          value={tools.length || '—'} sub="callable dal LLM" />
        </div>

        {/* Tabs */}
        <nav className="ac-tabs">
          <TabBtn k="inbox"   active={tab} set={setTab} icon={Inbox}     label="Founder Inbox"      badge={stats.open} />
          <TabBtn k="loop"    active={tab} set={setTab} icon={Zap}       label="Orchestrator Loop" />
          <TabBtn k="subs"    active={tab} set={setTab} icon={Activity}  label="Subscribers"        badge={subs.length} />
          <TabBtn k="tools"   active={tab} set={setTab} icon={Wrench}    label="Tools"              badge={tools.length} />
          <TabBtn k="improve" active={tab} set={setTab} icon={Sparkles}  label="Self-Improvement"   badge={improvementItems.length} />
        </nav>

        {/* ── INBOX ─────────────────────────────────────────────────── */}
        {tab === 'inbox' && (
          <section>
            <div className="ac-toolbar">
              {STATUS_FILTERS.map(f => (
                <button key={f.k}
                  className={`ac-chip ${statusFilter === f.k ? 'active' : ''}`}
                  onClick={() => setStatusFilter(f.k)}>{f.label}</button>
              ))}
              <span className="ac-chip-divider" />
              {SEVERITY_FILTERS.map(f => (
                <button key={f.k}
                  className={`ac-chip ${severityFilter === f.k ? 'active' : ''}`}
                  onClick={() => setSeverityFilter(f.k)}>{f.label}</button>
              ))}
              <span className="ac-toolbar-spacer" />
              <span className="ac-count-note">{filteredInbox.length} item{filteredInbox.length !== 1 ? 's' : ''}</span>
            </div>

            {filteredInbox.length === 0
              ? <Empty icon={Inbox} text="Inbox pulita ✨ — nessun item per i filtri selezionati." />
              : <div className="ac-inbox-list">
                  {filteredInbox.map(item => (
                    <InboxCard key={item._id} item={item} onAck={ackItem} onDismiss={dismissItem} />
                  ))}
                </div>
            }
          </section>
        )}

        {/* ── LOOP ──────────────────────────────────────────────────── */}
        {tab === 'loop' && (
          <section>
            <div className="ac-toolbar">
              <span className="ac-loop-info">
                Schedule: <code>*/30 * * * *</code> — ogni 30 min, orchestrator analizza KPI e dispatcha agenti.
              </span>
              <span className="ac-toolbar-spacer" />
              <button onClick={runLoopNow} disabled={loading} className="ac-btn primary">
                <Play size={13} /> Esegui ora
              </button>
            </div>

            {!loopLast
              ? <Empty icon={Zap} text="Nessuna esecuzione registrata ancora." />
              : <LoopCard run={loopLast} expanded />
            }

            {loopRecent.length > 1 && (
              <>
                <p className="ac-history-label">Storia recente</p>
                {loopRecent.slice(1).map(r => <LoopCard key={r._id} run={r} />)}
              </>
            )}
          </section>
        )}

        {/* ── SUBSCRIBERS ───────────────────────────────────────────── */}
        {tab === 'subs' && (
          <section>
            <p className="ac-subs-note">
              {subs.length} sottoscrizioni attive sul message bus.
              Ogni topic <code style={{ background: 'rgba(255,255,255,0.07)', padding: '1px 6px', borderRadius: 4, fontSize: 11, color: '#a1a1aa' }}>agentId:action</code> viene
              gestito dall'agente quando un evento viene pubblicato.
            </p>
            {subs.length === 0
              ? <Empty icon={Activity} text="Nessuna sottoscrizione registrata." />
              : <div className="ac-subs-list">
                  {subs.map((s, i) => {
                    const [agent, ...rest] = s.key.split(':');
                    return (
                      <div key={i} className="ac-sub-row">
                        <span className="ac-sub-agent">{agent}</span>
                        <ChevronRight size={13} color="#3f3f46" />
                        <code className="ac-sub-action">{rest.join(':')}</code>
                        <span className="ac-sub-count">{s.handlerCount} handler{s.handlerCount !== 1 ? 's' : ''}</span>
                      </div>
                    );
                  })}
                </div>
            }
          </section>
        )}

        {/* ── TOOLS ─────────────────────────────────────────────────── */}
        {tab === 'tools' && (
          <section>
            <p className="ac-tools-note">
              {tools.length} tools nel registry LLM.
              Ogni agente può invocare solo i tools nella propria allow-list (calcolata da PERMISSIONS).
            </p>
            {tools.length === 0
              ? <Empty icon={Wrench} text="Nessun tool registrato." />
              : <div className="ac-tools-grid">
                  {tools.map(t => {
                    const allowed = Object.entries(perms)
                      .filter(([, ps]) => ps.includes('*') || ps.includes(t.name))
                      .map(([a]) => a);
                    return (
                      <div key={t.name} className="ac-tool-card">
                        <h4 className="ac-tool-name"><code>{t.name}</code></h4>
                        <p className="ac-tool-desc">{t.description || 'Nessuna descrizione.'}</p>
                        <div className="ac-tool-agents">
                          {allowed.map(a => <span key={a} className="ac-tool-agent-pill">{a}</span>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </section>
        )}

        {/* ── SELF-IMPROVEMENT ──────────────────────────────────────── */}
        {tab === 'improve' && (
          <section>
            <p style={{ color: '#71717a', marginBottom: '1rem', fontSize: 13 }}>
              Review automatico settimanale (Lunedì 06:00).
              Le proposte di policy compaiono qui — accetta per applicare, scarta per ignorare.
            </p>
            {improvementItems.length === 0
              ? <Empty icon={Sparkles} text="Nessuna proposta in sospeso. Il prossimo review è Lunedì alle 06:00." />
              : <div className="ac-inbox-list">
                  {improvementItems.map(item => (
                    <InboxCard key={item._id} item={item} onAck={ackItem} onDismiss={dismissItem} showContext />
                  ))}
                </div>
            }
          </section>
        )}

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatTile({ icon, bg, label, value, sub }) {
  return (
    <div className="ac-stat">
      <div className="ac-stat-icon" style={{ background: bg }}>{icon}</div>
      <div>
        <p className="ac-stat-label">{label}</p>
        <p className="ac-stat-value">{value}</p>
        {sub && <p className="ac-stat-sub">{sub}</p>}
      </div>
    </div>
  );
}

function TabBtn({ k, active, set, icon: Icon, label, badge }) {
  return (
    <button onClick={() => set(k)} className={`ac-tab ${active === k ? 'active' : ''}`}>
      <Icon size={14} /> {label}
      {badge != null && badge !== 0 && (
        <span className="ac-tab-badge">{badge}</span>
      )}
    </button>
  );
}

function InboxCard({ item, onAck, onDismiss, showContext }) {
  const isOpen = ['pending', 'live'].includes(item.status);
  const ctx    = item.context && Object.keys(item.context).length > 0 ? item.context : null;
  return (
    <div className={`ac-inbox-card sev-${item.severity}`}>
      <div className="ac-card-row">
        <div className="ac-card-body">
          <div className="ac-card-meta">
            <span className="ac-sev-label">{item.severity}</span>
            <span className={`ac-status-pill ${item.status}`}>{item.status}</span>
            <span className="ac-meta-tag">{item.category}</span>
            <span className="ac-meta-tag">• {item.fromAgent}</span>
            <span className="ac-meta-tag">• {fmtDate(item.surfacedAt || item.createdAt)}</span>
          </div>
          <h3 className="ac-card-title">{item.title}</h3>
          <p className="ac-card-text">{item.body}</p>
          {showContext && ctx && (
            <div className="ac-card-context">
              <strong style={{ color: '#71717a' }}>Context: </strong>
              {Object.entries(ctx).map(([k, v]) => (
                <code key={k}>{k}={typeof v === 'object' ? JSON.stringify(v) : String(v)}</code>
              ))}
            </div>
          )}
        </div>
        <div className="ac-card-actions">
          {isOpen ? (
            <>
              <button onClick={() => onAck(item._id)}     className="ac-btn success sm"><Check size={12} /> Ack</button>
              <button onClick={() => onDismiss(item._id)} className="ac-btn ghost   sm"><X     size={12} /></button>
            </>
          ) : (
            <span style={{ fontSize: 11, color: '#52525b', alignSelf: 'center' }}>
              {item.acknowledgedBy || item.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function LoopCard({ run, expanded = false }) {
  const tools   = run.result?.toolCalls || [];
  const text    = run.result?.text;
  const iters   = run.result?.iterations;
  return (
    <div className="ac-loop-card">
      <div className="ac-loop-card-hd">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`ac-loop-badge ${run.status}`}>
            {run.status === 'completed' && <CheckCircle2 size={11} />}
            {run.status === 'failed'    && <XCircle      size={11} />}
            {' '}{run.status}
          </span>
          <span style={{ fontSize: 13, color: '#a1a1aa' }}>{fmtDate(run.createdAt)}</span>
        </div>
        <span style={{ fontSize: 12, color: '#52525b' }}>
          {run.duration ? `${run.duration} ms` : ''}{run.triggeredBy ? ` • ${run.triggeredBy}` : ''}
        </span>
      </div>

      <div className="ac-loop-meta">
        {iters !== undefined && <span><strong>Iterazioni:</strong> {iters}</span>}
        <span><strong>Tool calls:</strong> {tools.length}</span>
        {run.result?.provider && <span><strong>Provider:</strong> {run.result.provider}</span>}
        {run.error && <span style={{ color: '#f87171' }}><strong>Errore:</strong> {run.error}</span>}
      </div>

      {tools.length > 0 && (
        <div className="ac-loop-tools">
          {tools.map((tc, i) => (
            <span key={i} className={`ac-tool-chip ${tc.ok ? 'ok' : 'err'}`}>
              {tc.ok ? '✓' : '✗'} {tc.name}
            </span>
          ))}
        </div>
      )}

      {expanded && text && (
        <pre className="ac-loop-output">{text}</pre>
      )}
    </div>
  );
}

function Empty({ icon: Icon = Inbox, text }) {
  return (
    <div className="ac-empty">
      <Icon size={36} className="ac-empty-icon" />
      <p>{text}</p>
    </div>
  );
}
